import {
    Autocomplete,
    AutocompleteProps,
    CircularProgress,
    InputAdornment,
    StandardTextFieldProps,
    TextField,
    Typography,
} from "@mui/material"
import { ChipTypeMap } from "@mui/material/Chip"
import React, { useState } from "react"
import { WidthOpt } from "../../assets/inputStyles"
import { useFieldsStyles } from "./FieldsStyles"

interface NexAutocompleteProps<
    Value,
    Multiple extends boolean | undefined,
    DisableClearable extends boolean | undefined,
    FreeSolo extends boolean | undefined,
    ChipComponent extends React.ElementType,
> extends Omit<
        AutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>,
        "renderInput" | "onInputChange" | "inputValue"
    > {
    endAdornment?: React.ReactNode
    inline?: boolean
    maxLength?: number
    shrinkLabel?: boolean
    width?: WidthOpt
    loading?: boolean
    TextFieldProps?: StandardTextFieldProps
    testId?: string
}

export const NexAutocomplete = <
    Value,
    Multiple extends boolean | undefined = false,
    DisableClearable extends boolean | undefined = false,
    FreeSolo extends boolean | undefined = false,
    ChipComponent extends React.ElementType = ChipTypeMap["defaultComponent"],
>({
    endAdornment,
    TextFieldProps,
    inline,
    maxLength,
    shrinkLabel = false,
    width = "md",
    loading,
    testId,
    ...props
}: NexAutocompleteProps<Value, Multiple, DisableClearable, FreeSolo, ChipComponent>) => {
    const { classes: styles, cx } = useFieldsStyles()
    const [inputValue, setInputValue] = useState("")

    let helperText: React.ReactNode = TextFieldProps?.required && "Required"
    if (TextFieldProps?.helperText) {
        helperText = TextFieldProps?.helperText
    }

    return (
        <Autocomplete
            inputValue={inputValue}
            onInputChange={(_, newValue) => setInputValue(newValue)}
            autoSelect={props.freeSolo}
            renderInput={(params) => (
                <TextField
                    {...params}
                    {...TextFieldProps}
                    helperText={helperText && <Typography variant="caption">{helperText}</Typography>}
                    // The required attribute adds an asterisk which is irregular to our styling conventions
                    required={false}
                    error={TextFieldProps?.required && !props.value}
                    variant="outlined"
                    slotProps={{
                        htmlInput: {
                            ...params.inputProps,
                            ...TextFieldProps?.inputProps,
                            maxLength,
                            "data-testid": testId,
                        },
                        input: {
                            ...params.InputProps,
                            ...TextFieldProps?.InputProps,
                            endAdornment: (
                                <React.Fragment>
                                    {loading ? (
                                        <InputAdornment position="end">
                                            <CircularProgress color="inherit" size={20} />
                                        </InputAdornment>
                                    ) : null}
                                    {endAdornment && <InputAdornment position="end">{endAdornment}</InputAdornment>}
                                    {params.InputProps.endAdornment}
                                </React.Fragment>
                            ),
                        },
                        inputLabel: shrinkLabel ? { shrink: shrinkLabel } : undefined,
                    }}
                />
            )}
            className={cx({
                [styles[`input__${width}`]]: !props.fullWidth,
                [styles.inline]: inline,
            })}
            {...props}
        />
    )
}
